(ns minorodata.components.pipeline-editor.lists
  (:require
   ["react" :as react]
   [clojure.set :as c.set]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.dynamic-docs.sidebar :as dd.sidebar :refer [loading-placeholder
                                                                      transforms-not-found]]
   [minorodata.components.pipeline-editor.context-menu :refer [node-context-menu
                                                               transform-context-menu]]
   [minorodata.components.pipeline-editor.utils :refer [navigate-tab
                                                        navigate-extract-tab
                                                        navigate-table-tab
                                                        navigate-table-tab-preview
                                                        navigate-transform-tab
                                                        tab-icon]]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.sql-console.utils :refer [fetch-params
                                                    get-header-class
                                                    get-offset
                                                    node-title
                                                    node-type
                                                    type-icon]]
   [minorodata.components.tables.core :refer [->qualified-name]]
   [minorodata.sql-console.api :as sql-console.api]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.context-menu :refer [context-menu-content context-menu-root
                            context-menu-trigger]]
   [ui.tree :as ui.tree]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn on-transform-select [{:keys [master-id group-id name]} preview?]
  (navigate-transform-tab master-id group-id name preview?))

(defn tree-leaf [{:keys [active?] :as props} children]
  [:span
   (-> props
       (dissoc :active?)
       (update :class #(clsx "inline-block align-top w-full py-0.5 px-2 text-ellipsis whitespace-nowrap overflow-hidden rounded-lg transition-opacity cursor-pointer"
                             {"bg-kleene-selected-background ring ring-kleene-background" active?
                              "hover:opacity-60" (not active?)}
                             %)))
   children])

(defn transforms-list [{:keys [group-id]}]
  (when-not @(re-posh/subscribe [:sandbox.subs/enabled?])
    (dispatch [:pipeline-editor.api/fetch-kioku-transforms {:group-id group-id}]))

  (fn [{:keys [selected on-click]}]
    (let [transforms @(re-posh/subscribe [:pipeline-editor.subs/group-transforms group-id])
          loading?   (if @(re-posh/subscribe [:sandbox.subs/enabled?])
                       @(re-posh/subscribe [:mold-http/loading-id? :sandbox.api/fetch-sandbox-transforms])
                       @(re-posh/subscribe [:mold-http/loading-id? :pipeline-editor.api/fetch-kioku-transforms (keyword (str "fetch-pipeline-transforms-request-" group-id))]))
          not-found? (and group-id (not loading?) (empty? transforms))]
      (cond
        not-found? [transforms-not-found]
        loading?   [loading-placeholder]
        transforms (into [:div {:class "flex flex-col"}]
                         (for [{:keys [master-id group-id name] :as transform} (remove :stale transforms)]
                           ^{:key master-id}
                           [ui.tree/tree-node {}
                            [context-menu-root
                             [context-menu-trigger
                              [ui.tree/tree-row-trigger
                               {:leaf     true
                                :active   (selected master-id)
                                :on-click #(on-click transform)
                                :data-cy  (str "pe-picker-item-" name)}
                               [tab-icon {:type "transform"}]
                               [ui.tree/tree-row-label {:title name} name]]]
                             [context-menu-content
                              [transform-context-menu {:master-id master-id
                                                       :group-id  group-id
                                                       :name      name}]]]]))))))

(defonce group-open-cache (reagent/atom {}))

(defn group-collapsible [{:keys [group-id name selected on-click]}]
  (let [[expanded? set-expanded] (react/useState #(get @group-open-cache group-id false))]
    [ui.tree/tree-node
     {:open           expanded?
      :on-open-change (fn [value]
                        (swap! group-open-cache assoc group-id value)
                        (set-expanded value))}
     [ui.tree/tree-row-trigger {:data-cy (str "pe-picker-item-" name)}
      [ui.tree/tree-row-label {:title name :class "font-medium"} name]]
     [ui.tree/tree-node-content
      [transforms-list {:group-id group-id
                        :selected selected
                        :on-click on-click}]]]))

(defn transforms-content [{:keys [open-as-preview?]}]
  (let [loading? @(re-posh/subscribe [:mold-http/loading? :pipeline-editor.api/fetch-transform-groups])
        groups   @(re-posh/subscribe [:pipeline-editor.subs/transform-groups])
        selected (->> @(re-posh/subscribe [:pipeline-editor.subs/tabs])
                      (filter (comp #(= % "transform") :type))
                      (into #{} (map :id)))]
    (if loading?
      [c.spinner/with-text]
      [:div {:class "flex flex-col px-2"}
       (for [{:pipeline-group/keys [id name]} groups]
         ^{:key id}
         [:f> group-collapsible
          {:group-id  id
           :name      name
           :selected  selected
           :loading?  loading?
           :on-click  #(on-transform-select % open-as-preview?)}])])))

(defn on-extract-select [{:keys [master-id source-id name]} preview?]
  (navigate-extract-tab master-id source-id name preview?))

(defn extracts-list [{:keys [source-id]}]
  (dispatch [:connections.api/fetch-extracts {:source-id source-id :replace true}])

  (fn [{:keys [selected on-click]}]
    (let [extracts @(re-posh/subscribe [:pipeline-editor.subs/source-extracts source-id])
          loading? @(re-posh/subscribe [:mold-http/loading-id? :connections.api/fetch-extracts (keyword source-id)])
          none?    (and (not loading?) (empty? extracts))]
      (cond
        none?    [:div {:class "py-0.5 px-8 text-xs text-kleene-text-secondary"} "No extracts"]
        loading? [loading-placeholder]
        :else    (into [:div {:class "flex flex-col"}]
                       (for [{:keys [master-id name] :as extract} extracts]
                         ^{:key master-id}
                         [ui.tree/tree-node {}
                          [ui.tree/tree-row-trigger
                           {:leaf     true
                            :active   (selected master-id)
                            :on-click #(on-click extract)
                            :data-cy  (str "pe-picker-item-" name)}
                           [tab-icon {:type "extract"}]
                           [ui.tree/tree-row-label {:title name} name]]]))))))

(defn source-collapsible [{:keys [source-id name selected on-click]}]
  (let [[expanded? set-expanded] (react/useState #(get @group-open-cache source-id false))]
    [ui.tree/tree-node
     {:open           expanded?
      :on-open-change (fn [value]
                        (swap! group-open-cache assoc source-id value)
                        (set-expanded value))}
     [ui.tree/tree-row-trigger {:data-cy (str "pe-picker-item-" name)}
      [ui.tree/tree-row-label {:title name :class "font-medium"} name]]
     [ui.tree/tree-node-content
      [extracts-list {:source-id source-id
                      :selected  selected
                      :on-click  on-click}]]]))

(defn extracts-content [{:keys [open-as-preview?]}]
  (let [loading? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-sources])
        sources  @(re-posh/subscribe [:pipeline-editor.subs/sources])
        selected (->> @(re-posh/subscribe [:pipeline-editor.subs/tabs])
                      (filter (comp #(= % "extract") :type))
                      (into #{} (map :id)))]
    (cond
      (and loading? (empty? sources)) [c.spinner/with-text]
      (empty? sources) [:div {:class "py-2 px-2 text-sm text-kleene-text-secondary"} "No sources"]
      :else
      [:div {:class "flex flex-col px-2"}
       (for [{:keys [source-id name]} sources]
         ^{:key source-id}
         [:f> source-collapsible
          {:source-id source-id
           :name      name
           :selected  selected
           :on-click  #(on-extract-select % open-as-preview?)}])])))

(defonce tree-node-open-cache #js {})

(defn ->node-cache-key [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node) "." (:column-name node)))

(declare tree-node)

(defn tree-node-content [{:keys [params on-open selected preview?]}]
  (let [children   @(re-posh/subscribe [:sql-metadata-partial/prop
                                        [:sql-metadata-partial/params params]
                                        :sql-metadata-partial/metadata])
        loader-key (sql-console.api/->metadata-request-id params)
        loading?   @(re-posh/subscribe [:mold-http/loading-id? :sql-console.api/fetch-metadata loader-key])]
    [:<>
     (when loading?
       [:span {:class "ml-8 py-1 flex flex-row gap-2"}
        [c.spinner/inline-configurable {:size 14}]
        [typography/label {:class "whitespace-nowrap text-[10px] text-kleene-dark-05 dark:text-kleene-neutral-01 mt-0.5"}
         "Loading..."]])
     (into [:div {:class "flex flex-col"}]
           (for [s children
                 :let [qualified-name (->qualified-name (c.set/rename-keys s {:table-catalog :catalog
                                                                              :table-schem   :schema
                                                                              :table-name    :table}))]]
             (if (:table s)
               [tree-leaf {:key             qualified-name
                           :title           (:table s)
                           :class           "font-normal pl-6"
                           :active?         (when selected (selected qualified-name))
                           :on-click        #(if preview?
                                               (navigate-table-tab-preview qualified-name (str (:catalog s) "." (:schema s)) (:table s))
                                               (navigate-table-tab qualified-name (str (:catalog s) "." (:schema s)) (:table s)))
                           :on-double-click #(navigate-tab {:id       qualified-name
                                                            :type     "table"
                                                            :group-id (str (:catalog s) "." (:schema s))
                                                            :name     (:table s)
                                                            :preview  false})}
                (:table s)]
               [:f> tree-node {:key      qualified-name
                               :on-open  on-open
                               :node     s
                               :selected selected
                               :preview? preview?}])))]))

(defn tree-node [{:keys [node on-open selected preview?]}]
  (let [type             (node-type node)
        title            (node-title node)
        leaf?            (contains? #{"table" "column"} type)
        table?           (= type "table")
        catalog          (or (:table-catalog node) (:table-cat node))
        qualified-name   (str catalog "." (:table-schem node) "." (:table-name node))
        group-id         (str catalog "." (:table-schem node))
        params            (fetch-params node)
        loader-key        (sql-console.api/->metadata-request-id params)
        children-loading? @(re-posh/subscribe [:mold-http/loading-id? :sql-console.api/fetch-metadata loader-key])
        [open on-open-change] (ui.tree/use-cached-open-state tree-node-open-cache (->node-cache-key node))]

    [ui.tree/tree-node
     {:open           open
      :on-open-change (fn [v]
                        (on-open params)
                        (on-open-change v))
      :class          (get-offset type)}
     [context-menu-root
      [context-menu-trigger
       [ui.tree/tree-row-trigger
        {:leaf            leaf?
         :active          (and table? selected (selected qualified-name))
         :on-click        (when leaf? #(on-open params))
         :on-double-click (when table?
                            #(navigate-table-tab qualified-name group-id (:table-name node)))
         :row-class       (clsx "sticky transition-colors" (get-header-class type))
         :data-cy         (str "pe-picker-item-" title)}
        ;; A table IS a pipeline entity, so it gets the same coloured badge the
        ;; canvas nodes and the sidebar tabs use; catalogs/schemas are warehouse
        ;; structure, not entities, so they keep the plain structural icon.
        (if table?
          [tab-icon {:type "table"}]
          [ui.tree/tree-row-icon (type-icon type)])
        [ui.tree/tree-row-label {:title title} title]
        (when (and children-loading? (not open))
          [c.spinner/inline-configurable {:size 14}])]]
      [context-menu-content
       [node-context-menu
        (cond-> {:type type :node node}
          table?
          (assoc :preview-info
                 {:type     :table
                  :id       qualified-name
                  :group-id group-id}))]]]
     (when (not leaf?)
       [ui.tree/tree-node-content
        (when open
          [:f> tree-node-content {:params   params
                                  :on-open  on-open
                                  :selected selected
                                  :preview? preview?}])])]))

(defn tree-view-live [{:keys [children loading? selected-ids on-open preview?]}]
  (if loading?
    [c.spinner/with-text]
    [:div {:class "px-2 flex flex-col"}
     (for [i (range (count children))]
       ^{:key i}
       [:f> tree-node
        {:node     (nth children i)
         :selected selected-ids
         :preview? preview?
         :on-open
         (fn [{:keys [table schema catalog] :as params} callback]
           (if table
             (if preview?
               (navigate-table-tab-preview (str catalog "." schema "." table)
                                           (str catalog "." schema)
                                           table)
               (navigate-table-tab (str catalog "." schema "." table)
                                   (str catalog "." schema)
                                   table))
             (on-open params callback)))}])]))

(defn tables-content-live' [{:keys [catalogs selected-ids loading preview?]}]
  (let [fetch-catalog-tables (fn [params & [callbacks]]
                               (dispatch [:sql-console.api/fetch-metadata params callbacks]))]
    (react/useEffect
     (fn []
       (dispatch [:sql-console.api/fetch-metadata {:object "catalogs"}])
       js/undefined)
     #js [])

    [tree-view-live {:children     catalogs
                     :loading      loading
                     :selected-ids selected-ids
                     :preview?     preview?
                     :on-open      fetch-catalog-tables}]))

(defn tables-content-live [{:keys [open-as-preview?]}]
  (let [catalogs @(re-posh/subscribe [:sql-metadata-partial/prop
                                      [:sql-metadata-partial/params {:object "catalogs"}]
                                      :sql-metadata-partial/metadata])
        loading  (and (not catalogs)
                      @(re-posh/subscribe [:mold-http/loading? :sql-console.api/fetch-metadata]))
        tab-ids  (->> @(re-posh/subscribe [:pipeline-editor.subs/tabs])
                      (filter (comp #(= % "table") :type))
                      (mapv :id)
                      (into #{}))]
    [:f> tables-content-live' {:catalogs     catalogs
                               :selected-ids tab-ids
                               :loading      loading
                               :preview?     open-as-preview?}]))

(defn sandbox-tree-node [{:keys [name type path children selected preview?]}]
  (let [cache-key (str (:catalog path) "." name)
        [open on-open-change] (ui.tree/use-cached-open-state tree-node-open-cache cache-key)
        node      (case type
                    "catalog" {:table-cat name}
                    "schema"  {:table-cat (:catalog path) :table-schem name}
                    nil)]

    [ui.tree/tree-node
     {:open           open
      :on-open-change on-open-change
      :class          (get-offset type)}
     [context-menu-root
      [context-menu-trigger
       [ui.tree/tree-row-trigger
        {:row-class (clsx "sticky transition-colors" (get-header-class type))
         :data-cy   (str "pe-picker-item-" name)}
        [ui.tree/tree-row-icon (type-icon type)]
        [ui.tree/tree-row-label {:title name} name]]]
      [context-menu-content
       [node-context-menu {:type type :node node}]]]
     [ui.tree/tree-node-content
      (case type
        "catalog"
        (into [:div {:class "flex flex-col"}]
              (for [[schema tables] children]
                [:f> sandbox-tree-node {:key      schema
                                        :name     schema
                                        :type     "schema"
                                        :path     {:catalog name}
                                        :children tables
                                        :selected selected
                                        :preview? preview?}]))

        "schema"
        (into [:div {:class "flex flex-col"}]
              (for [{:keys [id table schema catalog]} children]
                ^{:key id}
                [ui.tree/tree-node
                 {:class (get-offset "table")}
                 [context-menu-root
                  [context-menu-trigger
                   [ui.tree/tree-row-trigger
                    {:leaf            true
                     :active          (when selected (selected id))
                     :row-class       (clsx "sticky transition-colors" (get-header-class "table"))
                     :on-click        #(if preview?
                                         (navigate-table-tab-preview id (str catalog "." schema) table)
                                         (navigate-table-tab id (str catalog "." schema) table))
                     :on-double-click #(navigate-tab {:id       id
                                                      :type     "table"
                                                      :group-id (str catalog "." schema)
                                                      :name     table})
                     :data-cy         (str "pe-picker-item-" table)}
                    [tab-icon {:type "table"}]
                    [ui.tree/tree-row-label {:title table} table]]]
                  [context-menu-content
                   [node-context-menu
                    {:type         "table"
                     :node         {:table-cat   catalog
                                    :table-schem schema
                                    :table-name  table}
                     :preview-info {:type     :table
                                    :id       id
                                    :group-id (str catalog "." schema)}}]]]])))]]))

(defn tables-content-sandbox' [{:keys [tree selected-ids preview? loading?]}]
  (react/useEffect
   (fn []
     (dispatch [:sandbox.api/fetch-sandbox-tables])
     js/undefined)
   #js [])

  (if loading?
    [c.spinner/with-text]
    [:div {:class "flex flex-col px-2"}
     (for [[catalog schemas] tree]
       [:f> sandbox-tree-node {:key      catalog
                               :name     catalog
                               :type     "catalog"
                               :children schemas
                               :selected selected-ids
                               :preview? preview?}])]))

(defn tables-content-sandbox [{:keys [open-as-preview?]}]
  (let [tree    @(re-posh/subscribe [:sandbox.subs/tables-tree])
        loading? (and (empty? tree)
                      @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-sandbox-tables]))
        tab-ids (->> @(re-posh/subscribe [:pipeline-editor.subs/tabs])
                     (filter (comp #(= % "table") :type))
                     (mapv :id)
                     (into #{}))]
    [:f> tables-content-sandbox' {:tree         tree
                                  :selected-ids tab-ids
                                  :preview?     open-as-preview?
                                  :loading?     loading?}]))

(defn tables-content
  [{:keys [open-as-preview?]}]
  (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])]
    (if sandbox?
      [:f> tables-content-sandbox {:open-as-preview? open-as-preview?}]
      [:f> tables-content-live {:open-as-preview? open-as-preview?}])))
